import React, { useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { DimmedBackground } from '../../../../components/common/dimmed/DimmedBackground';
import { searchAddressList, VWorldSearchItem, VWorldSearchResponse } from '../../../../api/vworld/searchAddress';
import { SpinnerLoader } from '../../../../components/common/loading/SpinnerLoader';
import { Icon } from '../../../../components/common/icon/Icon';
import { ampli } from '../../../../ampli';

export interface SearchAddressItemForFieldMap {
  address: string;
  coordinates: { x: number; y: number };
  pnu: string;
}

interface AddressSearchModalProps {
  isShow: boolean;
  onClose: () => void;
  onAddressSelected: (item: SearchAddressItemForFieldMap) => void;
}

export const AddressSearchModal = ({ isShow, onClose, onAddressSelected }: AddressSearchModalProps) => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchResult, setSearchResult] = useState<VWorldSearchResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);

  // 모달이 열릴 때마다 상태 초기화
  useEffect(() => {
    if (isShow) {
      setSearchQuery('');
      setSearchResult(null);
      setLoading(false);
      setCurrentPage(1);
    }
  }, [isShow]);

  // 검색 실행
  const handleSearch = useCallback(
    async (page: number = 1) => {
      if (!searchQuery.trim()) {
        alert('검색어를 입력해주세요.');
        return;
      }

      setLoading(true);
      try {
        const result = await searchAddressList(searchQuery.trim(), page, 30);
        setSearchResult(result);
        setCurrentPage(page);
        ampli.pvmClickSearchFieldButton({
          text: searchQuery,
          total: result.page.total,
        });
      } catch (error) {
        console.error('주소 검색 실패:', error);
        alert('주소 검색에 실패했습니다.');
      } finally {
        setLoading(false);
      }
    },
    [searchQuery],
  );

  // 주소 선택
  const handleAddressSelect = useCallback(
    (item: VWorldSearchItem) => {
      const selectedAddress = item.address.parcel || item.address.road;
      const coordinates = {
        x: parseFloat(item.point.x),
        y: parseFloat(item.point.y),
      };

      onAddressSelected({
        address: selectedAddress,
        coordinates,
        pnu: item.id,
      });
      onClose();
    },
    [onAddressSelected, onClose],
  );

  // 페이지 변경
  const handlePageChange = useCallback(
    (page: number) => {
      handleSearch(page);
    },
    [handleSearch],
  );

  // 페이지네이션 렌더링
  const renderPagination = () => {
    if (!searchResult) return null;

    const totalPages = parseInt(searchResult.page.total, 10);
    const current = parseInt(searchResult.page.current, 10);

    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisible = 5;

    let startPage = Math.max(1, current - Math.floor(maxVisible / 2));
    const endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    // 이전 페이지
    if (current > 1) {
      pages.push(
        <PageButton key="prev" onClick={() => handlePageChange(current - 1)}>
          &lt;
        </PageButton>,
      );
    }

    // 페이지 번호들
    for (let i = startPage; i <= endPage; i += 1) {
      pages.push(
        <PageButton key={i} $active={i === current} onClick={() => handlePageChange(i)}>
          {i}
        </PageButton>,
      );
    }

    // 다음 페이지
    if (current < totalPages) {
      pages.push(
        <PageButton key="next" onClick={() => handlePageChange(current + 1)}>
          &gt;
        </PageButton>,
      );
    }

    return <PaginationContainer>{pages}</PaginationContainer>;
  };

  return (
    <DimmedBackground isShow={isShow}>
      <Container
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <Header>
          <Title>주소 검색</Title>
          <CloseButton onClick={onClose}>
            <Icon name={'CloseGrey'} width={20} height={20} />
          </CloseButton>
        </Header>

        <SearchSection>
          <SearchInput
            type="text"
            placeholder="주소를 입력해주세요."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSearch(1);
              }
            }}
          />
          <SearchButton onClick={() => handleSearch(1)} disabled={loading}>
            {loading ? <SpinnerLoader isVisible={loading} /> : '검색'}
          </SearchButton>
        </SearchSection>

        <ResultSection>
          <ResultList>
            {searchResult?.result.items.map((item) => (
              <ResultItem key={item.id} onClick={() => handleAddressSelect(item)}>
                <Address>{item.address.parcel}</Address>
              </ResultItem>
            ))}
          </ResultList>

          {renderPagination()}
        </ResultSection>
      </Container>
    </DimmedBackground>
  );
};

const Container = styled.div`
  width: 90vw;
  max-width: 500px;
  height: 65vh;
  padding: 24px 0px;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  position: absolute;
  left: 50%;
  top: 50%;

  transform: translate(-50%, -50%);

  border-radius: var(--radius-large, 16px);
  background: var(--Light-Scheme-Monochrome-white, #fff);
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0px 24px;
  width: 100%;
`;

const Title = styled.h2`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 20.8px */
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SearchSection = styled.div`
  width: 100%;
  padding: 16px 24px;
  display: flex;
  gap: 6px;
  align-items: flex-start;
  align-self: stretch;
`;

const SearchInput = styled.input`
  display: flex;
  height: 40px;
  min-height: 40px;
  min-width: 0;
  padding: 10px 16px;
  justify-content: space-between;
  align-items: center;
  flex: 1;

  border-radius: var(--radius-medium, 8px);
  border: 1px solid var(--Light-Scheme-Monochrome-grey20, #ececf3);
  background: var(--Light-Scheme-Monochrome-white, #fff);

  /* Body/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 21px */

  &::placeholder {
    color: #a7a9b6;
  }
`;

const SearchButton = styled.button`
  display: flex;
  min-width: 60px;
  max-height: 40px;
  padding: 12px;
  justify-content: center;
  align-items: center;
  gap: 4px;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Color-primary-blue, #196eff);

  color: white;
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const ResultSection = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 0px 24px;
`;

const ResultList = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const ResultItem = styled.div`
  padding: 16px 0px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;

  &:hover {
    background-color: #f8f9fa;
  }
`;

const Address = styled.div`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);

  /* Body/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 21px */
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 12px;
  gap: 4px;
  border-top: 1px solid #f0f0f0;
`;

const PageButton = styled.button<{ $active?: boolean }>`
  padding: 8px 12px;
  border: 1px solid ${(props) => (props.$active ? '#196eff' : '#ddd')};
  background-color: ${(props) => (props.$active ? '#196eff' : 'white')};
  color: ${(props) => (props.$active ? 'white' : '#333')};
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  min-width: 36px;

  &:hover:not(:disabled) {
    background-color: ${(props) => (props.$active ? '#0056b3' : '#f8f9fa')};
  }
`;
